<!--

	This file is based on NuxtUI and modified to our needs
	https://github.com/nuxt/ui/blob/04f12adc5b6124c8371c256d2c40df2be832e6e2/src/runtime/components/ColorPicker.vue

	Modifications:
	* Support different orientations

	We will hopefully upstream the changes at some point.
-->

<!-- eslint-disable vue/block-tag-newline -->
<script lang="ts">
import type { AppConfig } from '@nuxt/schema';
import type { HSLObject } from 'colortranslator';
import type { MaybeRefOrGetter } from 'vue';
import type { ComponentConfig } from '#ui/types';
import theme from '#build/ui/color-picker';

type ColorPicker = ComponentConfig<typeof theme, AppConfig, 'colorPicker'>;

interface HSVColor {
	h: number;
	s: number;
	v: number;
}

function HSLtoHSV(hsl: HSLObject): HSVColor {
	const x = hsl.S * (hsl.L < 50 ? hsl.L : 100 - hsl.L);
	const v = hsl.L + (x / 100);

	return {
		h: hsl.H,
		s: hsl.L === 0 ? hsl.S : 2 * x / v,
		v,
	};
}

function HSVtoHSL(hsv: HSVColor): HSLObject {
	const x = (200 - hsv.s) * hsv.v / 100;

	return {
		H: hsv.h,
		S: x === 0 || x === 200 ? 0 : Math.round(hsv.s * hsv.v / (x <= 100 ? x : 200 - x)),
		L: Math.round(x / 2),
	};
}

export interface ColorPickerProps {
	/**
	 * The element or component this component should render as.
	 * @defaultValue 'div'
	 */
	as?: any;
	/**
	 * Throttle time in ms for the color picker
	 */
	throttle?: number;
	/**
	 * Disable the color picker
	 */
	disabled?: boolean;
	/**
	 * The default value of the color picker
	 */
	defaultValue?: string;
	/**
	 * Format of the color
	 * @defaultValue 'hex'
	 */
	format?: 'hex' | 'rgb' | 'hsl' | 'cmyk' | 'lab';
	/**
	 * @defaultValue 'md'
	 */
	size?: ColorPicker['variants']['size'];

	orientation?: 'horizontal' | 'vertical';

	class?: any;
	ui?: ColorPicker['slots'] & { trackContainer?: string };
}

</script>

<script setup lang="ts">
import { useElementBounding, useEventListener, watchPausable, watchThrottled } from '@vueuse/core';
import { isClient } from '@vueuse/shared';
import { ColorTranslator } from 'colortranslator';
import { Primitive } from 'reka-ui';
import { tv } from 'tailwind-variants';
import { computed, nextTick, ref, toRef, toValue, useTemplateRef, watch } from 'vue';
import { useAppConfig } from '#imports';

const props = withDefaults(defineProps<ColorPickerProps>(), {
	format: 'hex',
	throttle: 50,
	defaultValue: '#FFFFFF',
	orientation: 'vertical',
});
const modelValue = defineModel<string>();

const appConfig = useAppConfig() as ColorPicker['AppConfig'];

const ui = computed(() => tv({ extend: tv(theme), ...appConfig.ui?.colorPicker })({
	size: props.size,
	orientation: props.orientation,
}));

const pickedColor = computed<HSVColor>({
	get() {
		try {
			const color = new ColorTranslator(modelValue.value || props.defaultValue);

			return HSLtoHSV(color.HSLObject);
		}
		catch {
			return { h: 0, s: 0, v: 100 };
		}
	},
	set(value) {
		const color = new ColorTranslator(HSVtoHSL(value), {
			decimals: 2,
			labUnit: 'percent',
			cmykUnit: 'percent',
			cmykFunction: 'cmyk',
		});

		switch (props.format) {
			case 'rgb':
				modelValue.value = color.RGB;
				break;
			case 'hsl':
				modelValue.value = color.HSL;
				break;
			case 'cmyk':
				modelValue.value = color.CMYK;
				break;
			case 'lab':
				modelValue.value = color.CIELab;
				break;
			default:
				modelValue.value = color.HEX;
		}
	},
});

function useColorDraggable(targetElement: MaybeRefOrGetter<HTMLElement | null>, containerElement: MaybeRefOrGetter<HTMLElement | null>, axis: MaybeRefOrGetter<'x' | 'y' | 'both'>, initialPosition: MaybeRefOrGetter<{ x: number; y: number }>, disabled?: MaybeRefOrGetter<boolean | undefined>,
) {
	const position = ref<{ x: number; y: number }>(toValue(initialPosition));
	const pressedDelta = ref<{ x: number; y: number }>();
	const targetRect = useElementBounding(targetElement);
	const containerRect = useElementBounding(containerElement);

	watch(toRef(initialPosition), (newPosition, oldPosition) => {
		if (newPosition.x === oldPosition.y) {
			position.value = toValue(initialPosition);
		}
	});

	function start(event: PointerEvent) {
		if (toValue(disabled)) return event.preventDefault();

		const container = toValue(containerElement);

		pressedDelta.value = {
			x: event.clientX - (container ? event.clientX - containerRect.left.value + container.scrollLeft : targetRect.left.value),
			y: event.clientY - (container ? event.clientY - containerRect.top.value + container.scrollTop : targetRect.top.value),
		};

		move(event);
	}

	function move(event: PointerEvent) {
		if (!pressedDelta.value) return;

		const container = toValue(containerElement);
		const axisValue = toValue(axis);
		let { x, y } = position.value;

		if (container && (axisValue === 'x' || axisValue === 'both')) {
			x = Math.min(Math.max(0, (event.clientX - pressedDelta.value.x) / container.scrollWidth * 100), 100);
		}

		if (container && (axisValue === 'y' || axisValue === 'both')) {
			y = Math.min(Math.max(0, (event.clientY - pressedDelta.value.y) / container.scrollHeight * 100), 100);
		}

		position.value = { x, y };
	}

	function end() {
		if (!pressedDelta.value) {
			return;
		}

		pressedDelta.value = undefined;
	}

	if (isClient) {
		useEventListener(containerElement, 'pointerdown', start);
		useEventListener(window, 'pointermove', move);
		useEventListener(window, 'pointerup', end);
	}

	return {
		position,
		isDragging: computed(() => pressedDelta.value !== undefined),
	};
}

function normalizeHue(hue: number, dir: 'left' | 'right' = 'left'): number {
	if (dir === 'right') {
		return (hue * 100) / 360;
	}

	return (hue / 100) * 360;
}

function normalizeBrightness(brightness: number): number {
	return 100 - brightness;
}

const selectorRef = useTemplateRef<HTMLDivElement>('selectorRef');
const selectorThumbRef = useTemplateRef<HTMLDivElement>('selectorThumbRef');
const trackRef = useTemplateRef<HTMLDivElement>('trackRef');
const trackThumbRef = useTemplateRef<HTMLDivElement>('trackThumbRef');

const disabled = computed(() => props.disabled);

const currentTrackPosition = computed(() => props.orientation === 'vertical'
	? {
			x: 0,
			y: normalizeHue(pickedColor.value.h, 'right'),
		}
	: {
			x: normalizeHue(pickedColor.value.h, 'right'),
			y: 0,
		});

const { position: selectorThumbPosition } = useColorDraggable(selectorThumbRef, selectorRef, 'both', {
	x: pickedColor.value.s,
	y: normalizeBrightness(pickedColor.value.v),
}, disabled);

const { position: trackThumbPosition, isDragging: trackThumbIsDragging } = useColorDraggable(trackThumbRef, trackRef, () => props.orientation === 'vertical' ? 'y' : 'x', currentTrackPosition, disabled);

const { pause: pauseWatchColor, resume: resumeWatchColor } = watchPausable(pickedColor, (hsb) => {
	selectorThumbPosition.value = {
		x: hsb.s,
		y: normalizeBrightness(hsb.v),
	};

	trackThumbPosition.value = currentTrackPosition.value;
});

watchThrottled([selectorThumbPosition, trackThumbPosition], () => {
	pauseWatchColor();

	pickedColor.value = {
		h: normalizeHue(trackThumbPosition.value.x || trackThumbPosition.value.y),
		s: selectorThumbPosition.value.x,
		v: normalizeBrightness(selectorThumbPosition.value.y),
	};

	nextTick(resumeWatchColor);
}, { throttle: () => props.throttle });

const trackThumbColor = computed(() => new ColorTranslator(HSVtoHSL({
	h: normalizeHue(trackThumbPosition.value.x || trackThumbPosition.value.y),
	s: 100,
	v: 100,
})).HEX);

const selectorStyle = computed(() => ({
	backgroundColor: trackThumbColor.value,
}));

const selectorThumbColor = computed(() => new ColorTranslator(HSVtoHSL(pickedColor.value)).HEX);

const selectorThumbStyle = computed(() => ({
	backgroundColor: selectorThumbColor.value,
	left: `${selectorThumbPosition.value.x}%`,
	top: `${selectorThumbPosition.value.y}%`,
}));

const trackThumbStyle = computed(() => ({
	backgroundColor: trackThumbColor.value,
	left: `${trackThumbPosition.value.x}%`,
	top: `${trackThumbPosition.value.y}%`,
}));
</script>

<template>
	<Primitive :as :class="ui.root({ class: [props.ui?.root, props.class] })" :data-disabled="disabled ? true : undefined">
		<div :class="ui.picker({ class: props.ui?.picker })">
			<div
				ref="selectorRef"
				:class="ui.selector({ class: props.ui?.selector })"
				:style="selectorStyle"
			>
				<div :class="ui.selectorBackground({ class: props.ui?.selectorBackground })" data-color-picker-background>
					<div
						ref="selectorThumbRef"
						:class="ui.selectorThumb({ class: props.ui?.selectorThumb })"
						:style="selectorThumbStyle"
						:data-disabled="disabled ? true : undefined"
					/>
				</div>
			</div>
			<div :class="(ui as any).trackContainer({ class: props.ui?.trackContainer })">
				<slot name="track-leading" />
				<div
					ref="trackRef"
					:class="ui.track({ class: props.ui?.track })"
					data-color-picker-track
					:data-orientation="orientation"
				>
					<div
						ref="trackThumbRef"
						:class="ui.trackThumb({ class: props.ui?.trackThumb })"
						:style="trackThumbStyle"
						:data-disabled="disabled ? true : undefined"
						:data-dragging="String(trackThumbIsDragging)"
					/>
				</div>
				<slot name="track-trailing" />
			</div>
		</div>
	</Primitive>
</template>

<style scoped>
[data-color-picker-background] {
	background-image:
		linear-gradient(to top, #000 0%, rgba(0, 0, 0, 0) 100%),
		linear-gradient(to right, #fff 0%, rgba(255, 255, 255, 0) 100%);
}

[data-color-picker-track][data-orientation='vertical'] {
	background-image: linear-gradient(0deg, red 0, #f0f 17%, #00f 33%, #0ff 50%, #0f0 67%, #ff0 83%, red);
}
[data-color-picker-track][data-orientation='horizontal'] {
	background-image: linear-gradient(-90deg, red 0, #f0f 17%, #00f 33%, #0ff 50%, #0f0 67%, #ff0 83%, red);
}
</style>
